/* Barre de progression globale + toast de fin — voir hooks/use-exam-import-jobs.tsx.
   Montés une seule fois dans AppShell (visibles sur toutes les pages),
   rendu conditionnel : rien à afficher tant qu'aucun import n'est suivi. */

import { useEffect, useRef } from 'react';
import { CheckCircle, WarningCircle, X } from '@phosphor-icons/react';
import { Progress, ProgressIndicator, ProgressTrack } from './ui/progress';
import { useExamImportJobs } from '../hooks/use-exam-import-jobs';

const NOTICE_AUTO_DISMISS_MS = 5_000;

/* Libellés courts (2-3 mots), ton aligné sur STEP_LABEL de
   exam-copy-bulk-import-modal.tsx sans le copier verbatim (contexte
   différent : job de fond vs modale bloquante) — NAI-218. */
const STEP_LABEL: Record<string, string> = {
  generating: 'Analyse du document',
  saving: 'Finalisation',
  done: 'Terminé',
};

/* Mapping systématique failureKind → message FR actionnable — jamais le
 * job.error brut (qui peut porter une erreur Vertex technique telle quelle :
 * nom de modèle, région, code HTTP — repro bêta-testeur 16/10, 404
 * europe-west1). Même pattern que exam-copy-bulk-import-modal.tsx. Partagé
 * avec exam-import-modal.tsx via examImportFailureMessage ci-dessous. */
const FAILURE_KIND_LABEL: Record<string, string> = {
  appel_modele: "L'appel au modèle a échoué. Réessaie dans quelques instants.",
  json_invalide: "La réponse du modèle n'était pas exploitable. Réessaie l'import.",
  aucune_section: "Aucune section n'a pu être créée. Réessaie l'import.",
  variante_introuvable: "Une variante attendue est introuvable. Vérifie le document source.",
  structure_incoherente: "La structure obtenue est incohérente. Réessaie l'import.",
  couverture_partielle: "Certaines variantes ne sont pas couvertes. Réessaie l'import.",
  persistance: "L'enregistrement a échoué. Réessaie l'import.",
  interrompu: "Le traitement a été interrompu avant la fin. Réessaie l'import.",
};

const EXAM_IMPORT_FAILURE_FALLBACK = "La structuration a échoué. Réessaie dans quelques instants.";

/** Jamais le `error` brut du job (détails Vertex) — toujours un message FR
 *  actionnable, quel que soit le failureKind (y compris absent/inconnu). */
export function examImportFailureMessage(failureKind: string | null | undefined): string {
  return (failureKind && FAILURE_KIND_LABEL[failureKind]) || EXAM_IMPORT_FAILURE_FALLBACK;
}

function useRunningExamImportJob() {
  const { jobs } = useExamImportJobs();
  return jobs.find((j) => j.status === 'running') ?? null;
}

/* Rendu compact pour la sidebar gauche (header.tsx, juste avant Divider) —
   remplace l'ancienne pastille flottante (chevauchait le titre de page). */
export function ExamImportProgressRailItem() {
  const job = useRunningExamImportJob();
  if (!job) return null;

  const pct = job.sectionsTotal > 0 ? Math.min(100, Math.round((job.sectionsCreated / job.sectionsTotal) * 100)) : 0;
  const stepLabel = STEP_LABEL[job.step] ?? STEP_LABEL.generating;

  return (
    <div
      className="flex flex-col gap-1.5 mx-1 mb-2 px-3 py-2 rounded-[9px] bg-[var(--rail-surface)] border border-[var(--rail-line)]"
      role="status"
    >
      <span className="text-[12px] font-semibold text-[var(--rail-text-active)] truncate">{stepLabel}</span>
      <div className="flex items-center gap-2">
        <Progress value={pct} className="flex-1">
          <ProgressTrack>
            <ProgressIndicator className="bg-naia-blue" />
          </ProgressTrack>
        </Progress>
        <span className="text-[11.5px] font-semibold text-[var(--rail-text-muted)] tabular-nums shrink-0">{pct}%</span>
      </div>
    </div>
  );
}

export function ExamImportNoticeToast() {
  const { notice, dismissNotice } = useExamImportJobs();

  /* Clé logique de l'événement affiché (documentId+status) — distincte de la
     référence de `notice`, qui peut changer sans que l'événement affiché
     change (voir PR : pollDocument ne redéclenche pas setNotice pour un même
     job déjà terminal, mais on blinde quand même contre ce cas via la clé
     plutôt que la référence d'objet). Le timer ne (re)part que si la clé
     change réellement. */
  const noticeKey = notice ? `${notice.documentId}:${notice.status}` : null;
  const shownKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!noticeKey || shownKeyRef.current === noticeKey) return;
    shownKeyRef.current = noticeKey;
    const timer = window.setTimeout(() => dismissNotice(), NOTICE_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- dismissNotice est stable (useCallback sans deps) */
  }, [noticeKey]);

  useEffect(() => {
    if (!notice) shownKeyRef.current = null;
  }, [notice]);

  if (!notice) return null;

  const ok = notice.status === 'succeeded';
  const detail = !ok ? examImportFailureMessage(notice.failureKind) : null;

  /* Haut-droite sur grand écran ; pleine largeur (sous marge) en haut sur
     petit écran pour rester lisible à 360px sans déborder (NAI-218). */
  return (
    <div
      role="status"
      className="fixed inset-x-3 top-[calc(12px+env(safe-area-inset-top))] z-[60] mx-auto max-w-[420px] rounded-[14px] border border-border bg-card p-3 shadow-lg flex items-start gap-2 sm:inset-x-auto sm:right-[calc(12px+env(safe-area-inset-right))] sm:mx-0"
    >
      {ok ? (
        <CheckCircle size={20} weight="fill" className="shrink-0 text-[var(--naia-green-deep)]" />
      ) : (
        <WarningCircle size={20} weight="fill" className="shrink-0 text-[var(--naia-error)]" />
      )}
      <div className="flex-1 flex flex-col gap-0.5">
        <p className="m-0 text-[13.5px] font-bold text-[var(--naia-text-primary)]">
          {ok ? 'Import terminé' : "Échec de l'import"}
        </p>
        {detail && <p className="m-0 text-[12.5px] text-[var(--naia-text-secondary)]">{detail}</p>}
      </div>
      <button
        type="button"
        onClick={dismissNotice}
        aria-label="Fermer"
        className="shrink-0 flex items-center justify-center size-[30px] rounded-full hover:bg-naia-sky"
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}
